import { useNavigate } from 'react-router-dom'
import { useSubjectDifficultyTrends, useSubjectUnitWeightage } from '../../../hooks/usePYQAnalytics'
import { usePYQAnalyticsStore } from '../../../stores/pyqAnalyticsStore'
import LoadingSection from '../shared/LoadingSection'
import ErrorSection from '../shared/ErrorSection'
import SubjectTabs from '../shared/SubjectTabs'
import { DifficultyAreaChart } from '../charts'
import { SUBJECT_COLORS, SUBJECT_NAMES } from '../../../constants/colors'
import { AnalyticsLockedOverlay } from '@/features/payments'

interface SubjectAnalysisSectionProps {
  isLocked?: boolean
}

export default function SubjectAnalysisSection({ isLocked = false }: SubjectAnalysisSectionProps) {
  const navigate = useNavigate()
  const {
    yearRangeStart,
    yearRangeEnd,
    selectedSubject,
    setSelectedSubject,
  } = usePYQAnalyticsStore()

  const handleUnitClick = (unitCode: string) => {
    navigate(`/analytics/past-exams/unit/${unitCode}`)
  }

  const {
    data: difficultyData,
    isLoading: isDifficultyLoading,
    isError: isDifficultyError,
    refetch: refetchDifficulty,
  } = useSubjectDifficultyTrends(selectedSubject, {
    yearRangeStart,
    yearRangeEnd,
  })

  const {
    data: weightageData,
    isLoading: isWeightageLoading,
    isError: isWeightageError,
    refetch: refetchWeightage,
  } = useSubjectUnitWeightage(selectedSubject, {
    yearRangeStart,
    yearRangeEnd,
    sortBy: 'totalQuestions',
    sortOrder: 'desc',
  })

  const isLoading = isDifficultyLoading || isWeightageLoading
  const isError = isDifficultyError || isWeightageError

  if (isLoading) {
    return <LoadingSection title="Subject Analysis" height="h-[500px]" />
  }

  if (isError) {
    return (
      <ErrorSection
        title="Failed to load subject analysis"
        onRetry={() => {
          refetchDifficulty()
          refetchWeightage()
        }}
        height="h-[500px]"
      />
    )
  }

  const contentGrid = (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Difficulty Trends */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-4">
          Difficulty Distribution Over Time
        </h4>
        {difficultyData && difficultyData.data && (
          <>
            <DifficultyAreaChart data={difficultyData.data} showPercentage />
            {difficultyData.summary && (
              <div className="mt-4 flex items-center justify-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Overall Trend:</span>
                  <span
                    className={`font-medium ${
                      difficultyData.summary.overallTrend === 'GETTING_HARDER'
                        ? 'text-red-600'
                        : difficultyData.summary.overallTrend === 'GETTING_EASIER'
                          ? 'text-green-600'
                          : 'text-gray-600'
                    }`}
                  >
                    {difficultyData.summary.overallTrend === 'GETTING_HARDER'
                      ? 'Getting Harder'
                      : difficultyData.summary.overallTrend === 'GETTING_EASIER'
                        ? 'Getting Easier'
                        : 'Stable'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Avg Score:</span>
                  <span className="font-medium">
                    {difficultyData.summary.avgDifficultyScore?.toFixed(1) ?? 'N/A'}/10
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Unit Weightage */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-4">
          Unit Weightage in {SUBJECT_NAMES[selectedSubject]}
        </h4>
        {weightageData && weightageData.units && (
          <div className="space-y-3 max-h-[300px] overflow-y-auto">
            {weightageData.units.map((unit) => (
              <div
                key={unit.unitCode}
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => handleUnitClick(unit.unitCode)}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{unit.unitName || 'Unknown Unit'}</p>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                    <span>{unit.totalQuestions ?? 0} questions</span>
                    <span>•</span>
                    <span>{(unit.avgQuestionsPerExam ?? 0).toFixed(1)} avg/exam</span>
                  </div>
                </div>
                <div className="text-right">
                  <p
                    className="text-lg font-bold"
                    style={{ color: SUBJECT_COLORS[selectedSubject] }}
                  >
                    {(unit.percentageOfSubject ?? 0).toFixed(0)}%
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
        {weightageData && (
          <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-600">
            <p>
              Total: {weightageData.totalUnits} units, {weightageData.totalQuestions} questions
            </p>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header with Subject Tabs - always visible */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Subject Analysis</h3>
        <SubjectTabs selectedSubject={selectedSubject} onSubjectChange={setSelectedSubject} />
      </div>

      {/* Content - locked for free users */}
      {isLocked ? (
        <AnalyticsLockedOverlay>
          {contentGrid}
        </AnalyticsLockedOverlay>
      ) : (
        contentGrid
      )}
    </div>
  )
}
